import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';

const navLinks = [
  { label: 'Why Us?', href: '#problem' },
  { label: 'How It Works', href: '#solution' },
  { label: 'Success Stories', href: '#testimonials' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const sectionIds = ['hero', 'problem', 'solution', 'testimonials', 'pricing', 'faq'];
const mobileSections = [
  { id: 'hero', label: 'Home' },
  { id: 'problem', label: 'Why Us?' },
  { id: 'solution', label: 'How It Works' },
  { id: 'testimonials', label: 'Stories' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

export const LightSalesNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showMobilePills, setShowMobilePills] = useState(false);
  const [bubbleStyle, setBubbleStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const navRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const mobileBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setShowMobilePills(window.scrollY > 200);
      const navHeight = 100;
      let current: string | null = null;

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= navHeight + 50) {
          current = sectionIds[i];
          break;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update bubble position when active section changes
  useEffect(() => {
    if (!activeSection) {
      setBubbleStyle(prev => ({ ...prev, opacity: 0 }));
      return;
    }
    const idx = sectionIds.indexOf(activeSection);
    const btn = navRefs.current[idx];
    if (btn) {
      const parent = btn.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        setBubbleStyle({
          left: btnRect.left - parentRect.left,
          width: btnRect.width,
          opacity: 1,
        });
      }
    }
  }, [activeSection]);

  // Auto-scroll active mobile pill into view
  useEffect(() => {
    const activeIdx = mobileSections.findIndex(s => s.id === activeSection);
    const btn = mobileBtnRefs.current[activeIdx];
    if (btn && mobileScrollRef.current) {
      const container = mobileScrollRef.current;
      const btnLeft = btn.offsetLeft;
      const btnWidth = btn.offsetWidth;
      const containerWidth = container.offsetWidth;
      container.scrollTo({
        left: btnLeft - containerWidth / 2 + btnWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [activeSection]);

  const scrollToSection = (href: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        const navHeight = 60;
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementTop - navHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 px-4 py-2.5 bg-card/95 backdrop-blur-md border-b border-border shadow-sm"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center min-h-[44px] cursor-pointer"
          >
            <h1 className="text-lg font-semibold text-foreground">
              Faceless Aura <span className="text-primary">Club</span>
            </h1>
          </button>

          {/* Desktop Nav with glassy bubble */}
          <div className="hidden lg:flex items-center gap-1 relative">
            {/* Sliding glassy bubble */}
            <div
              className="absolute inset-y-0 flex items-center pointer-events-none"
              style={{
                left: bubbleStyle.left - 4,
                width: bubbleStyle.width + 8,
                opacity: bubbleStyle.opacity,
                transition: 'left 0.35s cubic-bezier(0.16, 1, 0.3, 1), width 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease',
              }}
            >
              <div className="w-full h-9 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/20" />
            </div>

            {navLinks.map((link, i) => (
              <button
                key={link.href}
                ref={(el) => { navRefs.current[i] = el; }}
                onClick={() => scrollToSection(link.href)}
                className={`relative z-10 text-sm transition-colors min-h-[44px] px-3 ${
                  activeSection === link.href.slice(1)
                    ? 'text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <TranslatedText>{link.label}</TranslatedText>
              </button>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <LanguageSelector variant="ghost" className="text-muted-foreground hover:text-foreground" />
            
            <Link to="/login" className="hidden sm:block">
              <Button 
                variant="ghost" 
                className="text-muted-foreground hover:text-foreground h-11 px-4"
              >
                <TranslatedText>Login</TranslatedText>
              </Button>
            </Link>
            <Link to="/apply?plan=free" className="hidden sm:block">
              <Button 
                className="border-2 border-primary/60 text-primary bg-primary/5 hover:bg-primary/10 active:scale-[0.98] h-11 px-4 text-sm font-semibold rounded-lg"
                variant="outline"
              >
                <TranslatedText>FREE TEST</TranslatedText>
              </Button>
            </Link>
            <Button 
              onClick={() => scrollToSection('#pricing')}
              className="bg-primary hover:bg-primary/90 active:scale-[0.98] text-primary-foreground h-11 px-5 text-sm font-semibold rounded-lg"
            >
              <TranslatedText>Get Started</TranslatedText>
            </Button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-3 -mr-2 text-muted-foreground min-w-[48px] min-h-[48px] flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile section pills - inside nav so no overlap */}
        {showMobilePills && !mobileMenuOpen && (
          <div
            ref={mobileScrollRef}
            className="lg:hidden flex items-center justify-center gap-1.5 px-3 py-2 overflow-x-auto border-t border-border"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {mobileSections.map((section, index) => (
              <button
                key={section.id}
                ref={(el) => { mobileBtnRefs.current[index] = el; }}
                onClick={() => {
                  if (section.id === 'hero') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    scrollToSection(`#${section.id}`);
                  }
                }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-300 active:scale-95 ${
                  activeSection === section.id || (!activeSection && section.id === 'hero')
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        )}
      </motion.nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-card lg:hidden pt-16"
        >
          <div className="p-6 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left py-4 px-4 text-lg text-foreground hover:bg-muted rounded-xl transition-colors active:bg-muted/80"
              >
                <TranslatedText>{link.label}</TranslatedText>
              </button>
            ))}
            <div className="pt-4 border-t border-border mt-4 space-y-2">
              <Link 
                to="/apply?plan=free" 
                className="block w-full py-4 px-4 text-center text-lg font-semibold text-primary bg-primary/5 border-2 border-primary/40 rounded-xl active:scale-[0.98]"
                onClick={() => setMobileMenuOpen(false)}
              >
                <TranslatedText>FREE TEST</TranslatedText>
              </Link>
              <Link 
                to="/login" 
                className="block w-full text-left py-4 px-4 text-lg text-muted-foreground hover:bg-muted rounded-xl"
                onClick={() => setMobileMenuOpen(false)}
              >
                <TranslatedText>Member Login</TranslatedText>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default LightSalesNav;
